/***
 * Created on 2024/2/20
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useEffect} from 'react';
import {useTranslation} from "react-i18next";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {apiGet, apiPatch} from "../../../../../service/api";
import {useCountdown} from "../../../../common/countdown";
import VerifyCode from "./verify_code";
import QnapAuthCheckStatus from "./qnap_auth_check_status";
import {useOnChange} from "../../../../../hook/form";
import {getLocalUser} from "../../../../../service/user";

function ShowQnapAuthAppQrcode({state, dispatch, modalRef}) {
    const { t } = useTranslation();
    const {seconds, setSeconds} = useCountdown(0);
    const [qrcode, setQrcode] = React.useState('');
    const queryClient = useQueryClient();
    const localUser = getLocalUser();
    const {onChange, values, setValues} = useOnChange({
        setDefault: true
    });
    const {data, status, isFetching} = useQuery({
        queryKey: ["get_qnap_auth_qrcode"],
        queryFn: async () => await apiGet(`/ajax/qnap_auth/qrcode?verify_type=${state.method}&is_binding=${true}`)
    });

    const showTimeleft = () => {
        if (seconds <= 59) {
            return `0:${String(seconds).padStart(2, '0')}`;
        }
        else {
            return `${Math.floor(seconds/60)}:${String(seconds%60).padStart(2, '0')}`;
        }
    }

    const onRefresh = async () => {
        setQrcode("");
        await queryClient.invalidateQueries({queryKey: ["get_qnap_auth_qrcode"]});
    }

    const setDefaultMutation = useMutation({
        mutationFn: async (data) => await apiPatch('/ajax/qnap_auth', data)
    })

    const onSetDefaultChanged = async (e) => {
        const is_default = !values.setDefault;
        setValues(prev => ({...prev, setDefault: !prev.setDefault}));
        await setDefaultMutation.mutateAsync({setDefault: is_default});

    }

    useEffect(() => {
        if (isFetching) {
            setQrcode("");
        }
        else if (status === 'success') {
            sessionStorage.setItem('timer', new Date().getTime().toString());
            setSeconds(300);
            setQrcode(data?.qrcode);
        }

        return () => {
            setQrcode("");
        }
    }, [status, data, isFetching]);

    return (
        <div className={`two-step--verify ${seconds <= 0 && !isFetching ? 'two-step--verify--time-out' : ""}`}>
            <div className="two-step--verify__qrcode">
                {!isFetching && <img src={qrcode || ""} alt=""/>}
                {isFetching &&
                    <div className="comp-placeholder w-full">
                        <div className="placeholder--qr-code"></div>
                    </div>
                }
            </div>
            <div className="two-step--verify__action">
                <p>{t('@Scan the QR code using "QNAP Authenticator" mobile app@')}</p>
                <div className="two-step--verify__action__count-down mt-10">
                    <div className="two-step--verify__action__count-down__ic"></div>
                    <div className="two-step--verify__action__count-down__time">{showTimeleft()}</div>
                </div>
                <p className="qr-code-expired color-red">{t('@The QR code set has expired. Please regenerate by clicking the button.@')}</p>
                <button className="q-btn q-btn--primary q-btn--hybrid btn-rounded-full mt-10">
                    <span className="q-btn__icon" ic-type="refresh"></span>
                    <span className="q-btn__label" onClick={onRefresh}>{t('@Refresh QR Code@')}</span>
                </button>

                <QnapAuthCheckStatus dispatch={dispatch} state={state} showPassCode={false}
                                     enable={state.method === 'approval'}/>

                {state?.method === 'verification' && <VerifyCode modalRef={modalRef} dispatch={dispatch}/>}

                <div className="comp-checkbox mt-24">
                    <label className="q-checkbox">
                        <input type="checkbox" name="setDefault"
                               value={values.setDefault}
                               checked={values.setDefault}
                               disabled={state?.setupAction === 'reset' && !localUser?.secure?.google_auth}
                               onChange={onSetDefaultChanged}/>
                        {t('@Set as default authentication method@')}
                    </label>
                </div>

            </div>

        </div>
    );
}

export default ShowQnapAuthAppQrcode;